<template>
  <Popup
    :isPopupOpen="otpState.visible"
    @close-popup="cancelOtp"
    class="bg-white w-clamp-20r-30r-100 radius-medium pd-medium"
  >
    <!-- Single mode -->
    <section v-if="otpState.mode !== 'dual'" class="t-center">
      <h3 class="mn-b-medium">
        {{ isEmailChallenge ? t('auth.enterCode.verifyEmailTitle') : t('auth.enterCode.verifyNumberTitle') }}
      </h3>

      <p class="mn-b-big opacity-50">
        {{ isEmailChallenge ? t('auth.enterCode.instructionsEmail') : t('auth.enterCode.instructions') }}
      </p>

      <p class="mn-b-small opacity-50">{{ otpState.challenge?.identifier }}</p>

      <div class="pos-relative mn-b-big cursor-text" @click="focusInput(0)">
        <input
          ref="inputRefs"
          v-model="code"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="4"
          class="pos-absolute inset-0 w-100 h-100 opacity-0 z-1"
          @input="onInput"
          @keydown="onKeydown"
          autocomplete="one-time-code"
        >
        <div class="d-flex flex-h-center gap-small">
          <div
            v-for="i in 4"
            :key="i"
            class="w-[60px] h-[60px] d-flex flex-center text-[28px] fw-medium bg-light radius-small br-1px ease-cubic"
            :class="code.length === i - 1 ? 'br-main' : 'br-grey'"
          >
            {{ code[i - 1] || '' }}
          </div>
        </div>
      </div>
    </section>

    <!-- Dual mode -->
    <section v-else class="t-center">
      <h3 class="mn-b-medium">{{ t('auth.enterCode.verifyChangeTitle') }}</h3>

      <div v-for="(challenge, idx) in otpState.challenges" :key="idx" class="mn-b-big">
        <p class="mn-b-thin opacity-50">
          {{ challenge.label === 'old' ? t('auth.enterCode.current') : t('auth.enterCode.new') }}
        </p>
        <p class="mn-b-small">{{ challenge.identifier }}</p>

        <div class="pos-relative cursor-text" @click="focusDualInput(idx)">
          <input
            :ref="el => dualInputRefs[idx] = el"
            v-model="dualCodes[idx]"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="4"
            class="pos-absolute inset-0 w-100 h-100 opacity-0 z-1"
            @input="onDualInput(idx)"
            @keydown="onKeydown"
            autocomplete="one-time-code"
          >
          <div class="d-flex flex-h-center gap-small">
            <div
              v-for="i in 4"
              :key="i"
              class="w-[48px] h-[48px] d-flex flex-center text-[24px] fw-medium bg-light radius-small br-1px ease-cubic"
              :class="dualCodes[idx].length === i - 1 ? 'br-main' : 'br-grey'"
            >
              {{ dualCodes[idx][i - 1] || '' }}
            </div>
          </div>
        </div>
      </div>

      <Button
        @click="submitDual"
        :disabled="!bothComplete"
        class="w-100 button--main"
      >
        {{ t('auth.enterCode.confirm') }}
      </Button>
    </section>
  </Popup>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Popup from '@martyrs/src/design-system/components/Popup/Popup.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue';
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  otpState: { type: Object, required: true },
  closeOtp: { type: Function, required: true },
  closeDualOtp: { type: Function, required: true },
  cancelOtp: { type: Function, required: true }
})

// Email-канал определяем по type ИЛИ по идентификатору с '@' — бэкенд не всегда
// проставляет challenge.type, из-за чего при регистрации по email показывался
// «Verify Phone».
const isEmailChallenge = computed(() =>
  props.otpState.challenge?.type === 'email' ||
  String(props.otpState.challenge?.identifier || '').includes('@')
)

const inputRefs = ref(null)
const dualInputRefs = ref([null, null])
const code = ref('')
const dualCodes = ref(['', ''])

const bothComplete = computed(() =>
  dualCodes.value[0].length === 4 && dualCodes.value[1].length === 4
)

watch(() => props.otpState.visible, (visible) => {
  if (visible) {
    code.value = ''
    dualCodes.value = ['', '']
    setTimeout(() => {
      if (props.otpState.mode === 'dual') {
        dualInputRefs.value[0]?.focus()
      } else {
        inputRefs.value?.focus()
      }
    }, 100)
  }
})

function focusInput() {
  inputRefs.value?.focus()
}

function focusDualInput(idx) {
  dualInputRefs.value[idx]?.focus()
}

function onKeydown(e) {
  if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key) && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
  }
}

function onInput() {
  code.value = code.value.replace(/\D/g, '').slice(0, 4)

  if (code.value.length === 4) {
    props.closeOtp(code.value)
  }
}

function onDualInput(idx) {
  dualCodes.value[idx] = dualCodes.value[idx].replace(/\D/g, '').slice(0, 4)

  // Auto-focus next input when current is complete
  if (dualCodes.value[idx].length === 4 && idx === 0) {
    dualInputRefs.value[1]?.focus()
  }

  // Auto-submit when both are complete
  if (bothComplete.value) {
    submitDual()
  }
}

function submitDual() {
  props.closeDualOtp([dualCodes.value[0], dualCodes.value[1]])
}
</script>
